import {
  InlineFormattedNumber,
  Tooltip,
  getColorBarColumn,
  parseDuration,
  useMixpanel,
  type ColorBarProps,
  type Column,
  type MixpanelEvent,
} from '@talos/kyoko';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import { calculateRelativeBarWidth } from '../PreTrade/utils';
import { ANALYTIC_PROPERTIES } from './constants';
import type { AnalyticName, TradePredictionRow } from './types';

function getColorBarProps(colId: string, data: TradePredictionRow): ColorBarProps {
  let barColor = '';
  let barWidth = 0;
  const { name, interval, daily } = data;
  const isNumericType = typeof interval !== 'string' && typeof daily !== 'string';

  switch (name) {
    case 'Spread':
      barColor = '#314A8C85';
      break;
    case 'Volume':
      barColor = '#DAA54185';
      break;
    case 'Participation rate':
      barColor = '#B351B385';
      break;
    case 'Volatility':
      barColor = '#C9444285';
      break;
    case 'Duration':
      barColor = '#97979B85';
      if (colId === 'daily') {
        barWidth = 1;
      } else if (colId === 'interval' && typeof interval === 'string') {
        const duration = parseDuration(interval);
        const durationMs =
          duration.days * 86400 + duration.hours * 3600 + duration.minutes * 60 + duration.seconds * 60;
        barWidth = Math.min(1, durationMs / 86400);
      }
      break;
    case 'End Time':
    case 'Start Time':
    case 'Residual Qty':
      break;
    default: {
      // This should never happen
      const _exhaustiveCheck: never = name;
    }
  }

  if (isNumericType && daily && interval) {
    switch (colId) {
      case 'interval':
        barWidth = calculateRelativeBarWidth(interval, daily);
        break;
      case 'daily':
        barWidth = calculateRelativeBarWidth(daily, interval);
        break;
    }
  }

  let unit = '';
  if (name === 'Participation rate' || colId === 'vs') {
    unit = '%';
  } else if (name === 'Spread' || name === 'Volatility') {
    unit = 'BPS';
  }
  const value = get(data, colId);

  return {
    content:
      value == null ? (
        '-'
      ) : isNumericType || colId === 'vs' ? (
        <InlineFormattedNumber number={value} currency={unit} increment="0.01" align="right" />
      ) : (
        <>{value}</>
      ),
    value,
    barColor,
    barWidth,
  };
}
export const useTradePredictionColumns = () => {
  const mixpanel = useMixpanel();

  return useMemo(() => {
    const columns: Column[] = [
      {
        type: 'custom',
        field: 'name',
        title: 'Analytic',
        params: {
          cellRenderer: ({ value }: { value: AnalyticName }) => {
            const tooltip =
              Object.values(ANALYTIC_PROPERTIES).find(prop => prop.name === value)?.tooltip ||
              `Analytic property: ${value}`;

            return (
              <Tooltip
                tooltip={tooltip}
                onOpen={() => {
                  mixpanel.track(`${value} Tooltip` as MixpanelEvent);
                }}
                showUnderline
              >
                {value}
              </Tooltip>
            );
          },
        },
      },
      {
        ...getColorBarColumn({ colId: 'interval', getColorBarProps }),
        title: 'Interval',
      },
      {
        ...getColorBarColumn({ colId: 'daily', getColorBarProps }),
        title: 'Daily',
      },
      {
        ...getColorBarColumn({ colId: 'vs', getColorBarProps }),
        title: 'VS',
      },
    ];
    return columns;
  }, [mixpanel]);
};
